import * as THREE from 'three';
import { VehiclePhysics } from './VehiclePhysics';
import { Cockpit } from './Cockpit';

export class CarModel {
  public mesh: THREE.Group;
  public cockpit: Cockpit;
  public wheels: THREE.Group[] = [];

  // Cabin & Roof meshes
  public roofMesh!: THREE.Mesh;
  public signGroup!: THREE.Group;
  public pillarsGroup: THREE.Group = new THREE.Group();
  public windowsGroup: THREE.Group = new THREE.Group();

  // Lights
  public headlights: THREE.SpotLight[] = [];
  public headlightMeshL!: THREE.Mesh;
  public headlightMeshR!: THREE.Mesh;
  public brakeLightsMesh!: THREE.Mesh;
  public reverseLightMesh!: THREE.Mesh;
  public indicatorL!: THREE.Mesh;
  public indicatorR!: THREE.Mesh;
  public rearIndicatorL!: THREE.Mesh;
  public rearIndicatorR!: THREE.Mesh;

  private bodyPaintMaterial: THREE.MeshStandardMaterial;

  constructor() {
    this.mesh = new THREE.Group();
    this.mesh.rotation.order = 'YXZ';
    this.cockpit = new Cockpit();

    this.bodyPaintMaterial = new THREE.MeshStandardMaterial({
      color: 0xf8fafc, // German Driving School Pure White (VW Golf / Polo style)
      metalness: 0.6,
      roughness: 0.3,
    });

    this.buildCarBody();
    this.buildWheels();
    this.buildLights();
    this.mesh.add(this.cockpit.group);
  }

  private buildCarBody() {
    const trimMat = new THREE.MeshStandardMaterial({ color: 0x0f172a, roughness: 0.8 });
    const interiorCardMat = new THREE.MeshStandardMaterial({ color: 0x1a202c, roughness: 0.9 });
    const doorMat = this.bodyPaintMaterial;

    // ── 1. Front Engine Hood / Bonnet (Motorhaube) ──────────────────────────
    // Slopes down towards front bumper; visible from driver seat for spatial positioning
    const hoodGeom = new THREE.BoxGeometry(1.76, 0.24, 1.25);
    const hoodMesh = new THREE.Mesh(hoodGeom, this.bodyPaintMaterial);
    hoodMesh.position.set(0, 0.60, 1.35);
    hoodMesh.rotation.x = -0.04;
    hoodMesh.castShadow = true;
    hoodMesh.receiveShadow = true;
    this.mesh.add(hoodMesh);

    // Front bumper & grille
    const frontBumperGeom = new THREE.BoxGeometry(1.80, 0.36, 0.35);
    const frontBumperMesh = new THREE.Mesh(frontBumperGeom, this.bodyPaintMaterial);
    frontBumperMesh.position.set(0, 0.36, 2.00);
    this.mesh.add(frontBumperMesh);

    const grilleMat = new THREE.MeshStandardMaterial({ color: 0x111827, roughness: 0.9 });
    const grilleGeom = new THREE.BoxGeometry(1.10, 0.16, 0.04);
    const grilleMesh = new THREE.Mesh(grilleGeom, grilleMat);
    grilleMesh.position.set(0, 0.46, 2.18);
    this.mesh.add(grilleMesh);

    // Front German License Plate (B · FS 2026)
    const plateTex = this.createGermanPlateTexture('B · FS 2026');
    const plateMat = new THREE.MeshStandardMaterial({ map: plateTex, roughness: 0.5 });
    const frontPlateMesh = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.13, 0.02), plateMat);
    frontPlateMesh.position.set(0, 0.28, 2.18);
    this.mesh.add(frontPlateMesh);

    // ── 2. Solid Floorboard & Aerodynamic Side Skirts ───────────────────────
    // Solid floorboard beneath cabin and cargo area
    const floorGeom = new THREE.BoxGeometry(1.72, 0.10, 2.70);
    const floorMat = new THREE.MeshStandardMaterial({ color: 0x1e242d, roughness: 0.95 });
    const floorMesh = new THREE.Mesh(floorGeom, floorMat);
    floorMesh.position.set(0, 0.18, -0.25);
    this.mesh.add(floorMesh);

    // Aerodynamic side skirts below doors
    const skirtGeom = new THREE.BoxGeometry(0.05, 0.12, 2.65);
    const skirtL = new THREE.Mesh(skirtGeom, trimMat);
    skirtL.position.set(0.85, 0.22, -0.20);
    const skirtR = new THREE.Mesh(skirtGeom, trimMat);
    skirtR.position.set(-0.85, 0.22, -0.20);
    this.mesh.add(skirtL, skirtR);

    // ── 3. Full-Height Side Doors & Interior Door Cards ────────────────────
    // Driver side door (Left = +X)
    const doorL = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.68, 1.85), doorMat);
    doorL.position.set(0.84, 0.54, -0.10);
    doorL.castShadow = true;
    doorL.receiveShadow = true;

    // Driver interior door card (soft-touch panel & molded armrest at elbow height)
    const doorCardL = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.66, 1.82), interiorCardMat);
    doorCardL.position.set(0.80, 0.54, -0.10);
    const armrestL = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.06, 0.50), interiorCardMat);
    armrestL.position.set(0.76, 0.62, 0.05);
    const handleL = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.03, 0.12), trimMat);
    handleL.position.set(0.77, 0.72, 0.18);

    // Passenger side door (Right = -X)
    const doorR = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.68, 1.85), doorMat);
    doorR.position.set(-0.84, 0.54, -0.10);
    doorR.castShadow = true;
    doorR.receiveShadow = true;

    const doorCardR = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.66, 1.82), interiorCardMat);
    doorCardR.position.set(-0.80, 0.54, -0.10);
    const armrestR = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.06, 0.50), interiorCardMat);
    armrestR.position.set(-0.76, 0.62, 0.05);
    const handleR = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.03, 0.12), trimMat);
    handleR.position.set(-0.77, 0.72, 0.18);

    // Solid rear quarter panels enclosing rear wheel arches & cargo bay (no cutouts)
    const rearQuarterGeom = new THREE.BoxGeometry(0.12, 0.68, 1.05);
    const rearQuarterL = new THREE.Mesh(rearQuarterGeom, doorMat);
    rearQuarterL.position.set(0.82, 0.54, -1.40);
    rearQuarterL.castShadow = true;
    const rearQuarterR = new THREE.Mesh(rearQuarterGeom, doorMat);
    rearQuarterR.position.set(-0.82, 0.54, -1.40);
    rearQuarterR.castShadow = true;

    // Enclosed rear bulkhead & luggage parcel shelf (completely seals cabin from cargo)
    const parcelShelfGeom = new THREE.BoxGeometry(1.64, 0.04, 0.75);
    const parcelShelf = new THREE.Mesh(parcelShelfGeom, interiorCardMat);
    parcelShelf.position.set(0, 0.86, -0.92);
    parcelShelf.castShadow = true;

    const bulkheadGeom = new THREE.BoxGeometry(1.64, 0.65, 0.06);
    const bulkhead = new THREE.Mesh(bulkheadGeom, interiorCardMat);
    bulkhead.position.set(0, 0.52, -0.58);

    this.mesh.add(
      doorL, doorCardL, armrestL, handleL,
      doorR, doorCardR, armrestR, handleR,
      rearQuarterL, rearQuarterR,
      parcelShelf, bulkhead
    );

    // ── 4. Pillars & Greenhouse (VW Golf Hatchback Frame) ───────────────────
    this.pillarsGroup = new THREE.Group();
    const pillarMat = this.bodyPaintMaterial;

    // Structural A-Pillars (Front Windshield Struts - Slender Golf/Polo profile)
    const aPillarGeom = new THREE.BoxGeometry(0.045, 0.88, 0.045);
    const aPillarL = new THREE.Mesh(aPillarGeom, pillarMat);
    aPillarL.position.set(0.78, 1.12, 0.46);
    aPillarL.rotation.x = -0.46;
    aPillarL.rotation.z = 0.07;
    this.pillarsGroup.add(aPillarL);

    const aPillarR = new THREE.Mesh(aPillarGeom, pillarMat);
    aPillarR.position.set(-0.78, 1.12, 0.46);
    aPillarR.rotation.x = -0.46;
    aPillarR.rotation.z = -0.07;
    this.pillarsGroup.add(aPillarR);

    // Structural B-Pillars (Center Struts between door windows)
    const bPillarGeom = new THREE.BoxGeometry(0.06, 0.60, 0.08);
    const bPillarL = new THREE.Mesh(bPillarGeom, trimMat);
    bPillarL.position.set(0.81, 1.16, -0.20);
    this.pillarsGroup.add(bPillarL);

    const bPillarR = new THREE.Mesh(bPillarGeom, trimMat);
    bPillarR.position.set(-0.81, 1.16, -0.20);
    this.pillarsGroup.add(bPillarR);

    // Characteristic VW Golf Broad C-Pillars (Rear Quarter Hatchback Pillars)
    // Thick solid quarter pillar sloping from roof to rear waistline
    const cPillarGeom = new THREE.BoxGeometry(0.14, 0.62, 0.44);
    const cPillarL = new THREE.Mesh(cPillarGeom, pillarMat);
    cPillarL.position.set(0.77, 1.16, -1.02);
    cPillarL.rotation.x = 0.30;
    this.pillarsGroup.add(cPillarL);

    const cPillarR = new THREE.Mesh(cPillarGeom, pillarMat);
    cPillarR.position.set(-0.77, 1.16, -1.02);
    cPillarR.rotation.x = 0.30;
    this.pillarsGroup.add(cPillarR);

    // ── 5. Solid Hatchback Tailgate & Rear Structure ────────────────────────
    // Lower Solid Tailgate Panel (Sheet metal below rear glass, waistline y=0.88 down to bumper y=0.46)
    const tailgateLowerGeom = new THREE.BoxGeometry(1.64, 0.44, 0.14);
    const tailgateLower = new THREE.Mesh(tailgateLowerGeom, pillarMat);
    tailgateLower.position.set(0, 0.68, -1.86);
    tailgateLower.rotation.x = 0.08;
    this.pillarsGroup.add(tailgateLower);

    // Upper Hatch Frame Struts enclosing rear glass
    const hatchStrutGeom = new THREE.BoxGeometry(0.12, 0.62, 0.08);
    const hatchStrutL = new THREE.Mesh(hatchStrutGeom, pillarMat);
    hatchStrutL.position.set(0.72, 1.16, -1.45);
    hatchStrutL.rotation.x = 0.42;
    this.pillarsGroup.add(hatchStrutL);

    const hatchStrutR = new THREE.Mesh(hatchStrutGeom, pillarMat);
    hatchStrutR.position.set(-0.72, 1.16, -1.45);
    hatchStrutR.rotation.x = 0.42;
    this.pillarsGroup.add(hatchStrutR);

    // Upper Hatch Header (Connecting roof to tailgate glass top)
    const hatchHeader = new THREE.Mesh(new THREE.BoxGeometry(1.56, 0.08, 0.14), pillarMat);
    hatchHeader.position.set(0, 1.44, -1.24);
    this.pillarsGroup.add(hatchHeader);

    // Lower Hatch Sill (Connecting lower tailgate to glass bottom)
    const hatchSill = new THREE.Mesh(new THREE.BoxGeometry(1.56, 0.08, 0.10), pillarMat);
    hatchSill.position.set(0, 0.88, -1.76);
    this.pillarsGroup.add(hatchSill);

    // Rear Roof Spoiler extending back over hatch glass
    const spoilerGeom = new THREE.BoxGeometry(1.56, 0.06, 0.26);
    const spoiler = new THREE.Mesh(spoilerGeom, trimMat);
    spoiler.position.set(0, 1.45, -1.26);
    this.pillarsGroup.add(spoiler);

    // Rear German License Plate Niche & Plate
    const plateNiche = new THREE.Mesh(new THREE.BoxGeometry(0.60, 0.18, 0.04), trimMat);
    plateNiche.position.set(0, 0.62, -1.935);
    this.pillarsGroup.add(plateNiche);

    const rearPlateMesh = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.13, 0.02), plateMat);
    rearPlateMesh.position.set(0, 0.62, -1.95);
    this.pillarsGroup.add(rearPlateMesh);

    // VW-style round chrome hatch handle / emblem
    const chromeMat = new THREE.MeshStandardMaterial({ color: 0xd4d4d8, metalness: 0.95, roughness: 0.15 });
    const emblemGeom = new THREE.CylinderGeometry(0.065, 0.065, 0.02, 20);
    emblemGeom.rotateX(Math.PI / 2);
    const emblem = new THREE.Mesh(emblemGeom, chromeMat);
    emblem.position.set(0, 0.79, -1.88);
    this.pillarsGroup.add(emblem);

    this.mesh.add(this.pillarsGroup);

    // ── 6. Full Solid Roof Top Plate ────────────────────────────────────────
    // Continuous solid metal roof panel covering entire cabin from windshield to hatch
    const roofGeom = new THREE.BoxGeometry(1.56, 0.04, 1.72);
    this.roofMesh = new THREE.Mesh(roofGeom, this.bodyPaintMaterial);
    this.roofMesh.position.set(0, 1.44, -0.40);
    this.roofMesh.castShadow = true;
    this.mesh.add(this.roofMesh);

    // Shark-fin roof antenna
    const antennaGeom = new THREE.ConeGeometry(0.03, 0.08, 4);
    antennaGeom.rotateX(-0.35);
    const antenna = new THREE.Mesh(antennaGeom, trimMat);
    antenna.position.set(0, 1.50, -1.05);
    this.mesh.add(antenna);

    // ── 7. Windshield & Windows (Glasshouse) ─────────────────────────────────
    this.windowsGroup = new THREE.Group();
    const glassMat = new THREE.MeshPhysicalMaterial({
      color: 0xe2e8f0,
      metalness: 0.1,
      roughness: 0.05,
      transmission: 0.92,
      transparent: true,
      opacity: 0.18,
      depthWrite: false,
    });

    // Dark privacy-tinted automotive rear glass (prevents seeing seats from behind)
    const rearGlassMat = new THREE.MeshPhysicalMaterial({
      color: 0x070b14,
      metalness: 0.85,
      roughness: 0.12,
      transmission: 0.10,
      transparent: true,
      opacity: 0.92,
      depthWrite: true,
    });

    // Front Windshield
    const windshieldGeom = new THREE.PlaneGeometry(1.46, 0.80);
    const windshield = new THREE.Mesh(windshieldGeom, glassMat);
    windshield.position.set(0, 1.12, 0.44);
    windshield.rotation.x = -0.46;
    this.windowsGroup.add(windshield);

    // Rear Hatch Glass (Seamlessly fitted inside hatch frame)
    const rearGlass = new THREE.Mesh(new THREE.PlaneGeometry(1.40, 0.62), rearGlassMat);
    rearGlass.position.set(0, 1.16, -1.45);
    rearGlass.rotation.x = 0.42;
    rearGlass.rotation.y = Math.PI;
    this.windowsGroup.add(rearGlass);

    // Side Windows (Driver side = +X, Passenger side = -X)
    const sideGlassL = new THREE.Mesh(new THREE.PlaneGeometry(1.55, 0.52), glassMat);
    sideGlassL.position.set(0.81, 1.14, -0.22);
    sideGlassL.rotation.y = -Math.PI / 2;
    this.windowsGroup.add(sideGlassL);

    const sideGlassR = new THREE.Mesh(new THREE.PlaneGeometry(1.55, 0.52), glassMat);
    sideGlassR.position.set(-0.81, 1.14, -0.22);
    sideGlassR.rotation.y = Math.PI / 2;
    this.windowsGroup.add(sideGlassR);

    this.mesh.add(this.windowsGroup);

    // ── 8. Sculpted Rear Bumper & Diffuser ─────────────────────────────────
    const rearBumperUpperGeom = new THREE.BoxGeometry(1.80, 0.26, 0.30);
    const rearBumperUpper = new THREE.Mesh(rearBumperUpperGeom, this.bodyPaintMaterial);
    rearBumperUpper.position.set(0, 0.38, -1.96);
    this.mesh.add(rearBumperUpper);

    const diffuserGeom = new THREE.BoxGeometry(1.80, 0.18, 0.26);
    const diffuser = new THREE.Mesh(diffuserGeom, trimMat);
    diffuser.position.set(0, 0.20, -1.98);
    this.mesh.add(diffuser);

    // Dual slim red bumper reflectors
    const reflectorMat = new THREE.MeshStandardMaterial({ color: 0x991b1b, roughness: 0.3 });
    const reflectorL = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.04, 0.02), reflectorMat);
    reflectorL.position.set(0.70, 0.28, -2.02);
    const reflectorR = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.04, 0.02), reflectorMat);
    reflectorR.position.set(-0.70, 0.28, -2.02);
    this.mesh.add(reflectorL, reflectorR);

    // ── 9. Roof Sign "FAHRSCHULE" ──────────────────────────────────────────
    this.signGroup = new THREE.Group();
    const signBarGeom = new THREE.BoxGeometry(0.85, 0.14, 0.22);
    const signBarMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.4 });
    const signBar = new THREE.Mesh(signBarGeom, signBarMat);

    const stripeGeom = new THREE.BoxGeometry(0.86, 0.04, 0.23);
    const stripeMat = new THREE.MeshBasicMaterial({ color: 0x2563eb });
    const stripe = new THREE.Mesh(stripeGeom, stripeMat);
    this.signGroup.add(signBar, stripe);

    this.signGroup.position.set(0, 1.55, -0.20);
    this.mesh.add(this.signGroup);
  }

  private createGermanPlateTexture(plateText: string): THREE.CanvasTexture {
    if (typeof document === 'undefined') {
      return new THREE.CanvasTexture({} as HTMLCanvasElement);
    }
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 128;
    const ctx = canvas.getContext('2d');
    if (!ctx) return new THREE.CanvasTexture(canvas);

    // White background & black outer border
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 512, 128);
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 6;
    ctx.strokeRect(3, 3, 506, 122);

    // Blue EU band on the left
    ctx.fillStyle = '#1d4ed8';
    ctx.fillRect(6, 6, 62, 116);

    // EU "D" letter
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 36px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('D', 37, 86);

    // 12 Yellow Stars circle
    ctx.fillStyle = '#facc15';
    for (let i = 0; i < 12; i++) {
      const angle = (i * Math.PI) / 6;
      const sx = 37 + Math.cos(angle) * 16;
      const sy = 34 + Math.sin(angle) * 16;
      ctx.beginPath();
      ctx.arc(sx, sy, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }

    // German plate text: "B  FS 2026"
    ctx.fillStyle = '#0f172a';
    ctx.font = 'bold 56px "DIN 1451", "Euro Plate", sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('B', 88, 72);

    // TÜV & Land seals (two small badges)
    ctx.fillStyle = '#16a34a'; // Green TÜV badge
    ctx.beginPath();
    ctx.arc(150, 50, 12, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#dc2626'; // Red state seal
    ctx.beginPath();
    ctx.arc(150, 82, 12, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#0f172a';
    ctx.fillText('FS 2026', 178, 72);

    const tex = new THREE.CanvasTexture(canvas);
    tex.anisotropy = 4;
    return tex;
  }

  private buildWheels() {
    const tireMat = new THREE.MeshStandardMaterial({
      color: 0x18181b,
      roughness: 0.85,
    });
    const rimMat = new THREE.MeshStandardMaterial({
      color: 0xd4d4d8,
      metalness: 0.85,
      roughness: 0.2,
    });

    for (let i = 0; i < 4; i++) {
      const wheelGroup = new THREE.Group();

      // Tire cylinder
      const tireGeom = new THREE.CylinderGeometry(0.32, 0.32, 0.22, 24);
      tireGeom.rotateZ(Math.PI / 2);
      const tireMesh = new THREE.Mesh(tireGeom, tireMat);
      tireMesh.castShadow = true;

      // Rim
      const rimGeom = new THREE.CylinderGeometry(0.22, 0.22, 0.23, 16);
      rimGeom.rotateZ(Math.PI / 2);
      const rimMesh = new THREE.Mesh(rimGeom, rimMat);

      // Wheel hub spokes
      const spokeGeom = new THREE.BoxGeometry(0.04, 0.4, 0.235);
      const spoke1 = new THREE.Mesh(spokeGeom, rimMat);
      const spoke2 = new THREE.Mesh(spokeGeom, rimMat);
      spoke2.rotation.x = Math.PI / 2;

      // Brake disc rotor & sporty red caliper behind spokes
      const discGeom = new THREE.CylinderGeometry(0.18, 0.18, 0.02, 20);
      discGeom.rotateZ(Math.PI / 2);
      const discMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8, metalness: 0.9, roughness: 0.25 });
      const discMesh = new THREE.Mesh(discGeom, discMat);
      discMesh.position.x = i % 2 === 0 ? 0.04 : -0.04;

      const caliperGeom = new THREE.BoxGeometry(0.04, 0.08, 0.05);
      const caliperMat = new THREE.MeshStandardMaterial({ color: 0xdc2626, roughness: 0.3 });
      const caliperMesh = new THREE.Mesh(caliperGeom, caliperMat);
      caliperMesh.position.set(i % 2 === 0 ? 0.04 : -0.04, 0.12, 0.06);

      wheelGroup.add(tireMesh, rimMesh, spoke1, spoke2, discMesh, caliperMesh);
      this.wheels.push(wheelGroup);
      this.mesh.add(wheelGroup);
    }
  }

  private buildLights() {
    // 1. Headlights (Front Left & Right)
    const headlightMat = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      emissive: 0xfffaed,
      emissiveIntensity: 0.8,
    });

    this.headlightMeshL = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.12, 0.05), headlightMat);
    this.headlightMeshL.position.set(-0.62, 0.52, 2.08);

    this.headlightMeshR = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.12, 0.05), headlightMat);
    this.headlightMeshR.position.set(0.62, 0.52, 2.08);

    // Front Light Spotlights
    const spotL = new THREE.SpotLight(0xfffaed, 3.5, 45, Math.PI / 6, 0.3, 1.2);
    spotL.position.set(-0.62, 0.55, 2.1);
    spotL.target.position.set(-0.62, 0.1, 20);

    const spotR = new THREE.SpotLight(0xfffaed, 3.5, 45, Math.PI / 6, 0.3, 1.2);
    spotR.position.set(0.62, 0.55, 2.1);
    spotR.target.position.set(0.62, 0.1, 20);

    this.headlights.push(spotL, spotR);
    this.mesh.add(this.headlightMeshL, this.headlightMeshR, spotL, spotL.target, spotR, spotR.target);

    // 2. Turn Indicators (Amber LEDs front & rear)
    const amberMat = new THREE.MeshStandardMaterial({
      color: 0xd97706,
      emissive: 0x000000,
    });
    this.indicatorL = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.08, 0.05), amberMat);
    this.indicatorL.position.set(-0.84, 0.52, 2.06);

    this.indicatorR = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.08, 0.05), amberMat.clone());
    this.indicatorR.position.set(0.84, 0.52, 2.06);

    this.rearIndicatorL = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.05, 0.03), amberMat);
    this.rearIndicatorL.position.set(0.66, 0.70, -1.93);

    this.rearIndicatorR = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.05, 0.03), amberMat.clone());
    this.rearIndicatorR.position.set(-0.66, 0.70, -1.93);

    this.mesh.add(this.indicatorL, this.indicatorR, this.rearIndicatorL, this.rearIndicatorR);

    // 3. Modern Split LED Taillight Clusters (VW Golf Style — Left & Right separate housings)
    const housingMat = new THREE.MeshStandardMaterial({ color: 0x450a0a, roughness: 0.4 });
    const brakeMat = new THREE.MeshStandardMaterial({
      color: 0x991b1b,
      emissive: 0xef4444,
      emissiveIntensity: 0.3,
    });

    // Left Taillight Cluster (x = +0.66, y = 0.74, z = -1.91)
    const housingL = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.16, 0.06), housingMat);
    housingL.position.set(0.66, 0.74, -1.91);

    const brakeMeshL = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.07, 0.03), brakeMat);
    brakeMeshL.position.set(0.66, 0.77, -1.93);

    // Right Taillight Cluster (x = -0.66, y = 0.74, z = -1.91)
    const housingR = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.16, 0.06), housingMat);
    housingR.position.set(-0.66, 0.74, -1.91);

    const brakeMeshR = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.07, 0.03), brakeMat);
    brakeMeshR.position.set(-0.66, 0.77, -1.93);

    // Third High-Mounted Brake Light (CHMSL integrated into the roof spoiler)
    this.brakeLightsMesh = new THREE.Mesh(new THREE.BoxGeometry(0.38, 0.035, 0.02), brakeMat);
    this.brakeLightsMesh.position.set(0, 1.46, -1.38);

    // 4. Reversing Light (White LED)
    const revMat = new THREE.MeshStandardMaterial({ color: 0x475569, emissive: 0x000000 });
    this.reverseLightMesh = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.05, 0.03), revMat);
    this.reverseLightMesh.position.set(0.50, 0.70, -1.93);

    this.mesh.add(
      housingL, brakeMeshL,
      housingR, brakeMeshR,
      this.brakeLightsMesh,
      this.reverseLightMesh
    );
  }

  public update(
    physics: VehiclePhysics,
    blinkerState: 'left' | 'right' | 'hazard' | 'off',
    brakeInput: number,
    isReversing: boolean,
    deltaTime: number = 0.016
  ) {
    // 1. Sync Chassis transform with Cannon-es body
    this.mesh.position.copy(physics.chassisBody.position as unknown as THREE.Vector3);
    this.mesh.quaternion.copy(physics.chassisBody.quaternion as unknown as THREE.Quaternion);

    // 2. Sync 4 Wheels
    for (let i = 0; i < physics.vehicle.wheelInfos.length; i++) {
      physics.vehicle.updateWheelTransform(i);
      const transform = physics.vehicle.wheelInfos[i].worldTransform;
      const wheelMesh = this.wheels[i];

      // Convert world transform to local vehicle coordinate
      const wheelWorldPos = transform.position as unknown as THREE.Vector3;
      const wheelWorldQuat = transform.quaternion as unknown as THREE.Quaternion;

      wheelMesh.position.copy(wheelWorldPos);
      wheelMesh.quaternion.copy(wheelWorldQuat);
      this.mesh.worldToLocal(wheelMesh.position);

      // Local rotation relative to chassis
      const invChassis = this.mesh.quaternion.clone().invert();
      wheelMesh.quaternion.copy(invChassis.multiply(wheelWorldQuat));
    }

    // 3. Update Brake Lights (Synchronously modulates all 3 brake lights)
    const isBraking = brakeInput > 0.05;
    const brakeMat = this.brakeLightsMesh.material as THREE.MeshStandardMaterial;
    brakeMat.emissiveIntensity = isBraking ? 2.5 : 0.3;

    // 4. Update Reversing Light
    const revMat = this.reverseLightMesh.material as THREE.MeshStandardMaterial;
    if (isReversing) {
      revMat.emissive.setHex(0xffffff);
      revMat.emissiveIntensity = 1.2;
    } else {
      revMat.emissive.setHex(0x000000);
      revMat.emissiveIntensity = 0;
    }

    // 5. Update Blinkers (Front & Rear Amber LEDs)
    const isBlinkOn = blinkerState !== 'off' && Math.floor(Date.now() / 380) % 2 === 0;
    const indLMat = this.indicatorL.material as THREE.MeshStandardMaterial;
    const indRMat = this.indicatorR.material as THREE.MeshStandardMaterial;

    const leftOn = (blinkerState === 'left' || blinkerState === 'hazard') && isBlinkOn;
    const rightOn = (blinkerState === 'right' || blinkerState === 'hazard') && isBlinkOn;

    indLMat.emissive.setHex(leftOn ? 0xf59e0b : 0x000000);
    indLMat.emissiveIntensity = leftOn ? 3.0 : 0.0;

    indRMat.emissive.setHex(rightOn ? 0xf59e0b : 0x000000);
    indRMat.emissiveIntensity = rightOn ? 3.0 : 0.0;

    if (this.rearIndicatorL) {
      const rearLMat = this.rearIndicatorL.material as THREE.MeshStandardMaterial;
      rearLMat.emissive.setHex(leftOn ? 0xf59e0b : 0x000000);
      rearLMat.emissiveIntensity = leftOn ? 3.0 : 0.0;
    }
    if (this.rearIndicatorR) {
      const rearRMat = this.rearIndicatorR.material as THREE.MeshStandardMaterial;
      rearRMat.emissive.setHex(rightOn ? 0xf59e0b : 0x000000);
      rearRMat.emissiveIntensity = rightOn ? 3.0 : 0.0;
    }

    // 6. Update Cockpit Dashboard Instruments
    const gearDisplay = physics.powertrain.getGearDisplay();
    this.cockpit.update(
      physics.currentSteeringAngle,
      physics.speedKmh,
      physics.powertrain.rpm,
      blinkerState,
      physics.powertrain.autoGear === 'P' || physics.isHandbrakeActive || physics.vehicle.wheelInfos[2].brake > 20,
      physics.powertrain.isStalled,
      gearDisplay,
      undefined,
      undefined,
      deltaTime
    );
  }
}
